// src/pages/overview/groups/GroupCreator.jsx
import React, { useState } from 'react';
import { useDealData } from 'contexts/DealContext';
import { usePortfolioData } from 'contexts/PortfolioContext';
import { useViewMode } from 'contexts/ViewModeContext';
import Card from 'components/card';
import { IoClose } from 'react-icons/io5';

const GroupCreator = ({ onClose }) => {
    const { viewMode } = useViewMode();
    const isPortfolioMode = viewMode === 'portfolio';

    // Use appropriate context based on view mode
    const dealData = useDealData();
    const portfolioData = usePortfolioData();

    const {
        currentDeal,
        currentDealName,
        createGroup,
        facilityGroups
    } = dealData;

    const {
        currentPortfolio,
        currentPortfolioName,
        createPortfolioGroup,
        portfolioFacilityGroups
    } = portfolioData;

    const [groupName, setGroupName] = useState('');
    const [selectedFacilities, setSelectedFacilities] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [isPrivate, setIsPrivate] = useState(true);

    // Get facility names from current deal/portfolio, excluding consolidated
    const facilities = isPortfolioMode
        ? (currentPortfolio
            ? Object.entries(currentPortfolio.facilities)
                .filter(([name]) => name.toLowerCase() !== 'consolidated')
                .map(([facilityName, facility]) => ({
                    id: facilityName,
                    name: facility.details?.Facilities || facilityName
                }))
            : [])
        : (currentDeal
            ? Object.entries(currentDeal.facilities)
                .filter(([name]) => name.toLowerCase() !== 'consolidated')
                .map(([facilityName, facility]) => ({
                    id: facilityName,
                    name: facility.details?.Facilities || facilityName
                }))
            : []);

    const handleCheckboxChange = (facilityId) => {
        setSelectedFacilities(prev => {
            if (prev.includes(facilityId)) {
                return prev.filter(id => id !== facilityId);
            } else {
                return [...prev, facilityId];
            }
        });
    };

    const handleSubmit = async () => {
        // Reset any previous errors
        setError('');

        if (!groupName.trim()) {
            setError('Please enter a group name');
            return;
        }

        if (selectedFacilities.length === 0) {
            setError('Please select at least one facility');
            return;
        }

        // Check for duplicate group names within the current deal/portfolio
        const isDuplicateName = isPortfolioMode
            ? portfolioFacilityGroups.some(group =>
                group.portfolioId === currentPortfolioName &&
                group.name.toLowerCase() === groupName.trim().toLowerCase()
            )
            : facilityGroups.some(group =>
                group.dealId === currentDealName &&
                group.name.toLowerCase() === groupName.trim().toLowerCase()
            );

        if (isDuplicateName) {
            const entityName = isPortfolioMode ? 'portfolio' : 'deal';
            setError(`A group named "${groupName}" already exists for this ${entityName}. Please choose a different name.`);
            return;
        }

        setIsSubmitting(true);

        try {
            const success = isPortfolioMode
                ? await createPortfolioGroup(groupName.trim(), selectedFacilities, isPrivate)
                : await createGroup(groupName.trim(), selectedFacilities, isPrivate);

            if (success) {
                onClose();
            } else {
                setError('Failed to create group. Please try again.');
            }
        } catch (err) {
            setError('An error occurred: ' + (err.message || 'Unknown error'));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Card extra="m-auto w-full md:max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold">
                    Create {isPortfolioMode ? 'Portfolio' : 'Facility'} Group
                </h3>
                <button
                    onClick={onClose}
                    className="p-1 rounded-full hover:bg-gray-100"
                >
                    <IoClose size={20} />
                </button>
            </div>

            {error && (
                <div className="mb-4 p-3 bg-red-100 text-red-800 rounded-md">
                    {error}
                </div>
            )}

            <div className="mb-4">
                <label className="block mb-2 font-medium">Group Name</label>
                <input
                    type="text"
                    value={groupName}
                    onChange={(e) => setGroupName(e.target.value)}
                    placeholder="Enter group name"
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
            </div>

            <div className="mb-6">
                <label className="block mb-2 font-medium">
                    Select Facilities in {isPortfolioMode ? currentPortfolioName : currentDealName}
                </label>
                {facilities.length === 0 ? (
                    <p className="text-gray-500">No facilities available</p>
                ) : (
                    <div className="max-h-60 overflow-y-auto border border-gray-300 rounded-md p-2">
                        {facilities.map(facility => (
                            <div key={facility.id} className="flex items-center mb-2">
                                <input
                                    type="checkbox"
                                    id={`facility-${facility.id}`}
                                    checked={selectedFacilities.includes(facility.id)}
                                    onChange={() => handleCheckboxChange(facility.id)}
                                    className="mr-2"
                                />
                                <label htmlFor={`facility-${facility.id}`}>{facility.name}</label>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="mb-6">
                <div className="flex items-center space-x-2">
                    <input
                        type="checkbox"
                        id="privacy-toggle"
                        checked={isPrivate}
                        onChange={(e) => setIsPrivate(e.target.checked)}
                        className="mr-2"
                    />
                    <label htmlFor="privacy-toggle">
                        Private (only you can see this group)
                    </label>
                </div>
            </div>

            <div className="flex justify-end space-x-2">
                <button
                    onClick={onClose}
                    className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                    disabled={isSubmitting}
                >
                    Cancel
                </button>
                <button
                    onClick={handleSubmit}
                    className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50"
                    disabled={isSubmitting}
                >
                    {isSubmitting ? 'Creating...' : 'Create Group'}
                </button>
            </div>
        </Card>
    );
};

export default GroupCreator;